import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useEffect, Suspense } from 'react';
import { lazyPage, preloadRoute } from '@/lib/lazyPage';
import { PageSkeleton } from '@/components/perf/PerfUI';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';
import '@/App.css';
import '@/styles/mobile-responsive.css';
import '@/utils/apiCache';       // transparent GET cache + dedup (side-effect import)

// Configure global axios defaults
axios.defaults.timeout = 15000;
// Iter 360 : session invalide/expiree -> deconnexion propre + retour login
// (evite le message brut "Invalid authentication" en boucle, ex: pointage PWA)
let sessionRedirecting = false;
const handleSessionExpired = (error) => {
  const status = error.response?.status;
  const detail = error.response?.data?.detail || '';
  const url = error.config?.url || '';
  // Iter 388 : abonnement magasin expiré → déconnexion immédiate de l'employé
  if (status === 403 && !url.includes('/auth/') && typeof detail === 'string' && detail.startsWith('Aucun abonnement actif')) {
    if (sessionRedirecting) return true;
    sessionRedirecting = true;
    localStorage.removeItem('token');
    toast.error(detail, { duration: 5000 });
    const subLoginPath = '/login';
    setTimeout(() => { window.location.href = subLoginPath; }, 1500);
    return true;
  }
  if (status !== 401 || url.includes('/auth/')) return false;
  if (!['Invalid authentication', 'User not found'].includes(detail)) return false;
  if (sessionRedirecting) return true;
  const path = window.location.pathname;
  const loginPath = '/login';
  if (path === loginPath) return true;
  sessionRedirecting = true;
  localStorage.removeItem('token');
  toast.error('Session expirée. Veuillez vous reconnecter.', { duration: 4000 });
  setTimeout(() => { window.location.href = loginPath; }, 1200);
  return true;
};
// Add retry interceptor for GET requests on network/server errors
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    handleSessionExpired(error);
    const config = error.config;
    if (config && !config._retry && !config.skipRetry && !axios.isCancel(error) && (!error.response || error.response.status >= 500) && config.method === 'get') {
      config._retry = true;
      await new Promise(r => setTimeout(r, 1500));
      return axios(config);
    }
    return Promise.reject(error);
  }
);

// Pages
const StoreDisplay = lazyPage('/store-display', () => import('@/pages/StoreDisplay'));
import Login from '@/pages/Login';
const EmployeeScanner = lazyPage('/employee/scanner', () => import('@/pages/EmployeeScanner'));
import EmployeeConfirm from '@/pages/EmployeeConfirm';
const EmployeeDashboard = lazyPage('/employee/dashboard', () => import('@/pages/EmployeeDashboard'));

const LegacyLoginRedirect = () => {
  const { enseigneSlug } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/login${enseigneSlug ? `/${enseigneSlug}` : ''}${search}`} replace />;
};
const ForgotPassword = lazyPage('/forgot-password', () => import('@/pages/ForgotPassword'));
const AdminDashboard = lazyPage('/admin/live', () => import('@/pages/AdminDashboard'));
const AdminHub = lazyPage('/admin/dashboard', () => import('@/pages/AdminHub'));
const AdminCameraTest = lazyPage('/admin/camera-test', () => import('@/pages/AdminCameraTest'));
const AdminMonthlyRecap = lazyPage('/admin/recap-mensuel', () => import('@/pages/AdminMonthlyRecap'));
const AdminCash = lazyPage('/employee/caisse', () => import('@/pages/AdminCash'));
const AdminEmployeesV2 = lazyPage('/admin/employees', () => import('@/pages/AdminEmployeesV2'));
const AdminRevenue = lazyPage('/admin/revenue', () => import('@/pages/AdminRevenue'));
const AdminGestionnaires = lazyPage('/admin/gestionnaires', () => import('@/pages/AdminGestionnaires'));
const AdminEmployeeProfile = lazyPage('/admin/employee/:employeeId', () => import('@/pages/AdminEmployeeProfile'));
const AdminEquipments = lazyPage('/admin/equipements', () => import('@/pages/AdminEquipments'));
const AdminHistory = lazyPage('/admin/history', () => import('@/pages/AdminHistory'));
const AdminStats = lazyPage('/admin/stats', () => import('@/pages/AdminStats'));
const AdminExports = lazyPage('/admin/exports', () => import('@/pages/AdminExports'));
const AdminAbsences = lazyPage('/admin/absences', () => import('@/pages/AdminAbsences'));
const AdminMessages = lazyPage('/admin/messages', () => import('@/pages/AdminMessages'));
const AdminComptables = lazyPage('/admin/comptables', () => import('@/pages/AdminComptables'));
const AdminValidation = lazyPage('/admin/validation', () => import('@/pages/AdminValidation'));
const AdminRegularizations = lazyPage('/admin/regularizations', () => import('@/pages/AdminRegularizations'));
const AdminProfile = lazyPage('/admin/profile', () => import('@/pages/AdminProfile'));
const AdminAttendanceValidation = lazyPage('/admin/attendance-validation', () => import('@/pages/AdminAttendanceValidation'));
const AdminPointages = lazyPage('/admin/pointages', () => import('@/pages/AdminPointages'));
const SuperadminStores = lazyPage('/admin/stores', () => import('@/pages/SuperadminStores'));
const SuperadminStoreAdmins = lazyPage('/admin/store-admins', () => import('@/pages/SuperadminStoreAdmins'));
const SuperadminGerants = lazyPage('/admin/gerants', () => import('@/pages/SuperadminGerants'));
const ComptablePortal = lazyPage('/comptable', () => import('@/pages/ComptablePortal'));
const HyperadminSessions = lazyPage('/admin/sessions', () => import('@/pages/HyperadminSessions'));
const HyperadminCredentials = lazyPage('/admin/credentials', () => import('@/pages/HyperadminCredentials'));
const AdminNotificationSettings = lazyPage('/admin/notification-settings', () => import('@/pages/AdminNotificationSettings'));
const AdminOvertimeRequests = lazyPage('/admin/overtime-requests', () => import('@/pages/AdminOvertimeRequests'));
const AdminMyStores = lazyPage('/admin/my-stores', () => import('@/pages/AdminMyStores'));
const AdminScheduleView = lazyPage('/employee/schedule', () => import('@/pages/AdminScheduleView'));
const AdminColorRotationPlanning = lazyPage('/admin/color-rotation-planning', () => import('@/pages/AdminColorRotationPlanning'));
const AdminCosts = lazyPage('/admin/costs', () => import('@/pages/AdminCosts'));
const AdminResponsableLeaves = lazyPage('/admin/responsable-leaves', () => import('@/pages/AdminResponsableLeaves'));
const AdminMyPayslips = lazyPage('/admin/my-payslips', () => import('@/pages/AdminMyPayslips'));
const AdminContracts = lazyPage('/admin/contracts', () => import('@/pages/AdminContracts'));
const AdminDocuments = lazyPage('/admin/documents', () => import('@/pages/AdminDocuments'));
const LandingPage = lazyPage('/', () => import('@/pages/LandingPage'));
const MentionsLegales = lazyPage('/mentions-legales', () => import('@/pages/MentionsLegales'));
const PolitiqueConfidentialite = lazyPage('/politique-confidentialite', () => import('@/pages/PolitiqueConfidentialite'));
const CGV = lazyPage('/cgv', () => import('@/pages/CGV'));
const HyperadminInvoices = lazyPage('/admin/ha-invoices', () => import('@/pages/HyperadminInvoices'));
const HyperadminSalesStats = lazyPage('/admin/ha-sales-stats', () => import('@/pages/HyperadminSalesStats'));
const HyperadminPhotoAttempts = lazyPage('/admin/photo-attempts', () => import('@/pages/HyperadminPhotoAttempts'));
const HyperadminPhotoMigration = lazyPage('/admin/photo-migration', () => import('@/pages/HyperadminPhotoMigration'));
const AdminSuppliers = lazyPage('/admin/suppliers', () => import('@/pages/AdminSuppliers'));
const AdminGame = lazyPage('/admin/game', () => import('@/pages/AdminGame'));
const HyperadminDemoRequests = lazyPage('/admin/demo-requests', () => import('@/pages/HyperadminDemoRequests'));
const AdminTasks = lazyPage('/admin/tasks', () => import('@/pages/AdminTasks'));
const AdminMissions = lazyPage('/admin/missions', () => import('@/pages/AdminMissions'));
const AdminDeliveries = lazyPage('/admin/deliveries', () => import('@/pages/AdminDeliveries'));
const AdminMerchandise = lazyPage('/employee/merchandise', () => import('@/pages/AdminMerchandise'));
const AdminInventory = lazyPage('/employee/inventory', () => import('@/pages/AdminInventory'));
const AdminTransport = lazyPage('/employee/transport', () => import('@/pages/AdminTransport'));
const AdminFacturation = lazyPage('/admin/facturation', () => import('@/pages/AdminFacturation'));
const QuoteSign = lazyPage('/devis/:token', () => import('@/pages/QuoteSign'));
const AdminCashHistory = lazyPage('/admin/depots-en-compte', () => import('@/pages/AdminCashHistory'));
// Iter 646 : « Dépôts en compte » fusionné dans l'espace Banque (gérants, gestionnaires, comptables, hyperadmin) ; les responsables gardent l'historique.
const DepotsRedirect = () => {
  const { user } = useAuth();
  if (user && ['super_admin', 'hyperadmin', 'admin', 'comptable'].includes(user.role)) return <Navigate to="/admin/banque?tab=depots" replace />;
  return <AdminCashHistory />;
};
const AdminBank = lazyPage('/admin/banque', () => import('@/pages/AdminBank'));
const AdminMandats = lazyPage('/admin/mandats', () => import('@/pages/AdminMandats'));
const AdminOrders = lazyPage('/admin/orders', () => import('@/pages/AdminOrders'));
const TutorialsPage = lazyPage('/admin/tutorials', () => import('@/pages/TutorialsPage'));
const HyperadminTutorialStudio = lazyPage('/admin/tutorial-studio', () => import('@/pages/HyperadminTutorialStudio'));
const EmployeeTasks = lazyPage('/employee/tasks', () => import('@/pages/EmployeeTasks'));
const AdminSubscription = lazyPage('/admin/subscription', () => import('@/pages/AdminSubscription'));
const HyperadminEnseignes = lazyPage('/admin/enseignes', () => import('@/pages/HyperadminEnseignes'));
const AdminNoSubscription = lazyPage('/admin/no-subscription', () => import('@/pages/AdminNoSubscription'));
const EmployeeContracts = lazyPage('/employee/contracts', () => import('@/pages/EmployeeContracts'));
const EmployeeEquipments = lazyPage('/employee/equipments', () => import('@/pages/EmployeeEquipments'));
const EmployeeHistory = lazyPage('/employee/history', () => import('@/pages/EmployeeHistory'));
const EnseigneSignup = lazyPage('/inscription-enseigne', () => import('@/pages/EnseigneSignup'));
const HyperadminEnseigneRequests = lazyPage('/admin/enseigne-requests', () => import('@/pages/HyperadminEnseigneRequests'));
import { EnseigneEmployeeRoute, EnseigneAdminRoute } from '@/components/EnseigneShortcuts';
import ProtectedRoute from '@/components/ProtectedRoute';
import { AdminLayoutOutlet } from '@/components/AdminLayout';
import usePageTracking from '@/hooks/usePageTracking';
import { useAuth } from '@/hooks/useAuth';
import EmployeeShell from '@/components/navigation/EmployeeShell';
import VersionGate from '@/components/VersionGate';

function PageTracker() {
  usePageTracking();
  return null;
}

// Iter 455 : theme clair PARTOUT par defaut. Le mode sombre est une option
// (ui_theme='dark') appliquee UNIQUEMENT sur les pages admin. Les interfaces
// employe, kiosque QR et pages publiques restent toujours en clair.
function ThemeGuard() {
  const location = useLocation();
  useEffect(() => {
    const isAdmin = location.pathname.startsWith('/admin');
    let dark = false;
    try { dark = localStorage.getItem('ui_theme') === 'dark'; } catch {}
    document.documentElement.classList.toggle('dark', isAdmin && dark);
  }, [location.pathname]);
  return null;
}

function App() {
  useEffect(() => {
    // Préchargement en arrière-plan de la page d'accueil du rôle connecté
    let role = null;
    try { role = JSON.parse(localStorage.getItem('user') || 'null')?.role; } catch { /* ignore */ }
    const target = role === 'employee' ? '/employee/dashboard' : role ? '/admin/dashboard' : null;
    if (!target) return;
    const run = () => { preloadRoute(target); if (role !== 'employee') { preloadRoute('/admin/employees'); preloadRoute('/admin/attendance'); } };
    const id = 'requestIdleCallback' in window ? window.requestIdleCallback(run, { timeout: 3000 }) : setTimeout(run, 1500);
    return () => ('cancelIdleCallback' in window ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <PageTracker />
        <ThemeGuard />
        <Suspense fallback={<div className="min-h-screen p-6"><PageSkeleton /></div>}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/inscription-enseigne" element={<EnseigneSignup />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
          <Route path="/cgv" element={<CGV />} />
          <Route path="/devis/:token" element={<QuoteSign />} />
          
          {/* Store Display */}
          <Route path="/store/:storeId" element={<StoreDisplay />} />
          
          {/* Employee Routes */}
          {/* Connexion unique (email ou téléphone + mot de passe ou PIN) ; anciennes URL redirigées */}
          <Route path="/login" element={<Login />} />
          <Route path="/login/:enseigneSlug" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/employee/auth" element={<LegacyLoginRedirect />} />
          <Route path="/employee/auth/:enseigneSlug" element={<LegacyLoginRedirect />} />
          <Route element={<EmployeeShell />}>
            <Route path="/employee/scan" element={<ProtectedRoute role="employee"><EmployeeScanner /></ProtectedRoute>} />
            <Route path="/employee/confirm" element={<ProtectedRoute role="employee"><EmployeeConfirm /></ProtectedRoute>} />
            <Route path="/employee/dashboard" element={<ProtectedRoute role="employee"><EmployeeDashboard /></ProtectedRoute>} />
            <Route path="/employee/schedule" element={<ProtectedRoute role="employee"><AdminScheduleView /></ProtectedRoute>} />
            {/* Iter 313 : Caisse accessible aux employes avec caisse_permissions */}
            <Route path="/employee/caisse" element={<ProtectedRoute role="employee"><AdminCash /></ProtectedRoute>} />
            {/* Iter 528 : Transferts marchandise accessibles aux employes avec merch_permissions */}
            <Route path="/employee/merchandise" element={<ProtectedRoute role="employee"><AdminMerchandise /></ProtectedRoute>} />
            <Route path="/employee/inventory" element={<ProtectedRoute role="employee"><AdminInventory /></ProtectedRoute>} />
            <Route path="/employee/transport" element={<ProtectedRoute role="employee"><AdminTransport /></ProtectedRoute>} />
          </Route>
          
          {/* Admin Login (no layout) */}
          <Route path="/admin/login" element={<LegacyLoginRedirect />} />
          <Route path="/admin/login/:enseigneSlug" element={<LegacyLoginRedirect />} />
          <Route path="/admin/forgot-password" element={<Navigate to="/forgot-password" replace />} />

          {/* All admin pages share a PERSISTENT AdminLayout */}
          <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayoutOutlet /></ProtectedRoute>}>
            <Route path="stores" element={<SuperadminStores />} />
            <Route path="store-admins" element={<SuperadminStoreAdmins />} />
            <Route path="gerants" element={<SuperadminGerants />} />
            <Route path="gestionnaires" element={<AdminGestionnaires />} />
            <Route path="comptables" element={<AdminComptables />} />
            <Route path="dashboard" element={<AdminHub />} />
            <Route path="live" element={<AdminDashboard />} />
            <Route path="camera-test" element={<AdminCameraTest />} />
            <Route path="recap-mensuel" element={<AdminMonthlyRecap />} />
            <Route path="employees" element={<AdminEmployeesV2 />} />
            <Route path="revenue" element={<AdminRevenue />} />
            <Route path="employee/:employeeId" element={<AdminEmployeeProfile />} />
            <Route path="equipements" element={<AdminEquipments />} />
            <Route path="absences" element={<AdminAbsences />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="validation" element={<AdminValidation />} />
            <Route path="history" element={<AdminPointages />} />
            <Route path="stats" element={<AdminStats />} />
            <Route path="exports" element={<AdminExports />} />
            <Route path="regularizations" element={<AdminPointages />} />
            <Route path="attendance-validation" element={<AdminPointages />} />
            <Route path="pointages" element={<AdminPointages />} />
            <Route path="attendance" element={<AdminAttendanceValidation />} />
            <Route path="profile" element={<AdminProfile />} />
            <Route path="sessions" element={<HyperadminSessions />} />
            <Route path="credentials" element={<HyperadminCredentials />} />
            <Route path="my-stores" element={<AdminMyStores />} />
            <Route path="overtime-requests" element={<AdminPointages />} />
            <Route path="notification-settings" element={<AdminNotificationSettings />} />
            <Route path="schedules" element={<AdminScheduleView />} />
            <Route path="color-rotation-planning" element={<AdminColorRotationPlanning />} />
            <Route path="costs" element={<AdminCosts />} />
            <Route path="responsable-leaves" element={<AdminResponsableLeaves />} />
            <Route path="my-payslips" element={<AdminMyPayslips />} />
            <Route path="contracts" element={<AdminContracts />} />
            <Route path="documents" element={<AdminDocuments />} />
            <Route path="enseignes" element={<HyperadminEnseignes />} />
            <Route path="enseigne-requests" element={<HyperadminEnseigneRequests />} />
            <Route path="subscription" element={<AdminSubscription />} />
            <Route path="no-subscription" element={<AdminNoSubscription />} />
            <Route path="ha-invoices" element={<HyperadminInvoices />} />
            <Route path="ha-sales-stats" element={<HyperadminSalesStats />} />
            <Route path="photo-attempts" element={<HyperadminPhotoAttempts />} />
            <Route path="photo-migration" element={<HyperadminPhotoMigration />} />
            <Route path="suppliers" element={<AdminSuppliers />} />
            <Route path="game" element={<AdminGame />} />
            <Route path="demo-requests" element={<HyperadminDemoRequests />} />
            <Route path="tasks" element={<AdminTasks />} />
            <Route path="missions" element={<AdminMissions />} />
            <Route path="deliveries" element={<AdminDeliveries />} />
            <Route path="merchandise" element={<AdminMerchandise />} />
            <Route path="inventory" element={<AdminInventory />} />
            <Route path="transport" element={<AdminTransport />} />
            <Route path="facturation" element={<AdminFacturation />} />
            <Route path="depots-en-compte" element={<DepotsRedirect />} />
            <Route path="clotures-de-caisse" element={<AdminCashHistory />} />
            <Route path="banque" element={<AdminBank />} />
            <Route path="mandats" element={<AdminMandats />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="tutorials" element={<TutorialsPage />} />
            <Route path="caisse" element={<AdminCash />} />
            <Route path="tutorial-studio" element={<HyperadminTutorialStudio />} />
          </Route>
          
          {/* Comptable Routes */}
          <Route path="/comptable" element={<ProtectedRoute role="comptable"><ComptablePortal /></ProtectedRoute>} />
          <Route path="/comptable/facturation" element={<ProtectedRoute role="comptable"><AdminFacturation /></ProtectedRoute>} />
          
          {/* Employee contract view */}
          <Route element={<EmployeeShell />}>
            <Route path="/employee/contracts" element={<ProtectedRoute role="employee"><EmployeeContracts /></ProtectedRoute>} />
            <Route path="/employee/equipments" element={<ProtectedRoute role="employee"><EmployeeEquipments /></ProtectedRoute>} />
            <Route path="/employee/history" element={<ProtectedRoute role="employee"><EmployeeHistory /></ProtectedRoute>} />
            <Route path="/employee/tasks" element={<ProtectedRoute role="employee"><EmployeeTasks /></ProtectedRoute>} />
          </Route>
          
          {/* Enseigne short URLs (catch-all, must be LAST) */}
          {/* logyra.fr/maa -> employee auth, logyra.fr/maa/admin -> admin login */}
          <Route path="/:enseigneSlug/admin" element={<EnseigneAdminRoute />} />
          <Route path="/:enseigneSlug" element={<EnseigneEmployeeRoute />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
      <VersionGate />
    </div>
  );
}

export default App;
